import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, inr } from "../../lib/api";
import { PageHeader, Card, Loading, Empty, StatusBadge, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Building2, ChevronRight, ArrowLeft, Users, IndianRupee, Layers, CalendarDays,
  Download, Plus, Pencil, Trash2, Loader2, GraduationCap, Filter, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";

export default function Schools() {
  const [view, setView] = useState("schools"); // schools | school | batch
  const [schools, setSchools] = useState(null);
  const [school, setSchool] = useState(null);      // selected school {id,name}
  const [breakdown, setBreakdown] = useState(null);
  const [block, setBlock] = useState(null);        // selected program/level block
  const [batch, setBatch] = useState(null);        // selected batch
  const [students, setStudents] = useState(null);
  const [excludeLevel, setExcludeLevel] = useState("");
  const [progLevels, setProgLevels] = useState([]);

  // add/edit dialog
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", city: "", state: "", contact_person: "", contact_number: "" });
  const [saving, setSaving] = useState(false);

  const loadSchools = useCallback(() => {
    setSchools(null);
    api.get("/schools/overview").then((r) => setSchools(r.data)).catch(() => setSchools([]));
  }, []);
  useEffect(() => { loadSchools(); }, [loadSchools]);

  const openSchool = async (sc) => {
    setSchool(sc); setView("school"); setBreakdown(null);
    try { const { data } = await api.get(`/schools/${sc.id}/breakdown`); setBreakdown(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openBatch = async (blk, bt) => {
    setBlock(blk); setBatch(bt); setView("batch"); setStudents(null); setExcludeLevel("");
    try {
      const [{ data }, lv] = await Promise.all([
        api.get(`/schools/${school.id}/students`, { params: { program_id: blk.program_id, level_id: blk.level_id, batch_id: bt.batch_id || undefined } }),
        api.get(`/levels`, { params: { program_id: blk.program_id } }),
      ]);
      setStudents(data);
      setProgLevels(lv.data.filter((l) => l.id !== blk.level_id));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const applyExclude = async (levelId) => {
    setExcludeLevel(levelId); setStudents(null);
    try {
      const { data } = await api.get(`/schools/${school.id}/students`, { params: { program_id: block.program_id, level_id: block.level_id, batch_id: batch.batch_id || undefined, exclude_level_id: levelId || undefined } });
      setStudents(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/schools/${school.id}/students/export`, {
        params: { fmt, program_id: block.program_id, level_id: block.level_id, batch_id: batch.batch_id || undefined, exclude_level_id: excludeLevel || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `students.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url); toast.success("Downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  // school CRUD
  const openNew = () => { setForm({ name: "", city: "", state: "", contact_person: "", contact_number: "" }); setEditing(null); setDlg(true); };
  const openEdit = (sc, e) => { e.stopPropagation(); setForm({ name: sc.name, city: sc.city || "", state: "", contact_person: "", contact_number: "" }); setEditing(sc); setDlg(true); };
  const saveSchool = async () => {
    if (!form.name) { toast.error("School name is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/schools/${editing.id}`, form); else await api.post("/schools", form);
      toast.success("Saved"); setDlg(false); loadSchools();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const removeSchool = async (sc, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${sc.name}"?`)) return;
    try { await api.delete(`/schools/${sc.id}`); toast.success("Deleted"); loadSchools(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  // ---------- SCHOOLS LIST ----------
  if (view === "schools") {
    return (
      <div>
        <PageHeader title="Schools" subtitle="Payment & enrollment overview per school. Click a school to drill down.">
          <button className={btnPrimary} onClick={openNew} data-testid="school-add-btn"><Plus className="w-4 h-4" /> Add School</button>
        </PageHeader>
        {schools === null ? <Loading /> : schools.length === 0 ? <Empty label="No schools yet" /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {schools.map((sc) => (
              <button key={sc.id} onClick={() => openSchool(sc)} data-testid="school-card"
                className="text-left bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:border-blue-300 hover:shadow-md transition-all group">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span onClick={(e) => openEdit(sc, e)} className="w-7 h-7 rounded-lg hover:bg-slate-100 text-slate-400 flex items-center justify-center cursor-pointer"><Pencil className="w-3.5 h-3.5" /></span>
                    <span onClick={(e) => removeSchool(sc, e)} className="w-7 h-7 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center cursor-pointer"><Trash2 className="w-3.5 h-3.5" /></span>
                  </div>
                </div>
                <div className="mt-3 font-head font-bold text-slate-900 text-lg leading-tight">{sc.name}</div>
                {sc.city && <div className="text-xs text-slate-400 mt-0.5">{sc.city}</div>}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Stat icon={GraduationCap} label="Students" value={sc.students} />
                  <Stat icon={IndianRupee} label="Collected" value={inr(sc.revenue)} />
                  <Stat icon={Layers} label="Programs" value={sc.programs} />
                  <Stat icon={CalendarDays} label="Batches" value={sc.batches} />
                </div>
                <div className="mt-3 text-blue-600 text-sm font-medium flex items-center gap-1">View details <ChevronRight className="w-4 h-4" /></div>
              </button>
            ))}
          </div>
        )}
        <SchoolDialog dlg={dlg} setDlg={setDlg} editing={editing} form={form} setForm={setForm} saving={saving} save={saveSchool} />
      </div>
    );
  }

  // ---------- SCHOOL -> PROGRAM/LEVEL BLOCKS + BATCHES ----------
  if (view === "school") {
    return (
      <div>
        <Breadcrumb items={[{ label: "Schools", onClick: () => { setView("schools"); loadSchools(); } }, { label: school?.name }]} />
        {breakdown === null ? <Loading /> : (
          <>
            <div className="flex justify-end mb-4">
              <button className={btnPrimary} onClick={() => navigate(`/admin/schools/${school.id}/students`)} data-testid="view-students-btn"><Users className="w-4 h-4" /> View / Filter Students</button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <BigStat icon={GraduationCap} label="Students" value={breakdown.stats.students} color="text-sky-600 bg-sky-50" />
              <BigStat icon={IndianRupee} label="Amount Collected" value={inr(breakdown.stats.revenue)} color="text-emerald-600 bg-emerald-50" />
              <BigStat icon={Layers} label="Programs" value={breakdown.stats.programs} color="text-violet-600 bg-violet-50" />
              <BigStat icon={ClipboardCount} label="Registrations" value={breakdown.stats.registrations} color="text-blue-600 bg-blue-50" />
            </div>
            <h3 className="font-head font-semibold text-slate-900 mb-3">Programs conducted</h3>
            {breakdown.blocks.length === 0 ? <Empty label="No registrations for this school yet" /> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {breakdown.blocks.map((blk, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Layers className="w-4 h-4" /></div>
                      <div>
                        <div className="font-semibold text-slate-800 leading-tight">{blk.program_name}</div>
                        <div className="text-xs text-slate-400">{blk.level_name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-2 mb-3">
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {blk.students}</span>
                      <span className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> {inr(blk.revenue)}</span>
                    </div>
                    <div className="space-y-1.5">
                      {blk.batches.map((bt) => (
                        <button key={bt.batch_id} onClick={() => openBatch(blk, bt)} data-testid="batch-card"
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-200 transition-all text-sm">
                          <span className="font-medium text-slate-700 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-slate-400" /> {bt.batch_name}</span>
                          <span className="text-slate-400 flex items-center gap-2">{bt.students} students <ChevronRight className="w-4 h-4" /></span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ---------- BATCH -> STUDENTS ----------
  return (
    <div>
      <Breadcrumb items={[
        { label: "Schools", onClick: () => { setView("schools"); loadSchools(); } },
        { label: school?.name, onClick: () => openSchool(school) },
        { label: `${block?.program_name} · ${block?.level_name} · ${batch?.batch_name}` },
      ]} />
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600 flex-1">
            <Filter className="w-4 h-4 text-slate-400" />
            <span>Show students who have <span className="font-semibold">NOT</span> registered for:</span>
            <select className={`${inputCls} sm:w-56`} value={excludeLevel} onChange={(e) => applyExclude(e.target.value)} data-testid="exclude-level-filter">
              <option value="">— All students —</option>
              {progLevels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button className={btnSecondary} onClick={() => download("xlsx")} data-testid="download-students-xlsx"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
            <button className={btnPrimary} onClick={() => download("csv")} data-testid="download-students-csv"><Download className="w-4 h-4" /> CSV</button>
          </div>
        </div>
      </Card>
      <Card>
        {students === null ? <Loading /> : students.length === 0 ? <Empty label="No students match this view" /> : (
          <div className="overflow-x-auto">
            <div className="px-5 py-3 text-sm text-slate-500 border-b border-slate-100">{students.length} student{students.length !== 1 ? "s" : ""}</div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                {["Student", "Standard", "Division", "Contact", "This Batch", "Levels Registered", "Payment"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="school-student-row">
                    <td className="px-5 py-3.5 font-medium text-slate-800">{s.student_name}<div className="text-xs text-slate-400 font-mono">{s.registration_id}</div></td>
                    <td className="px-5 py-3.5 text-slate-600">{s.standard || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-600">{s.division || "—"}</td>
                    <td className="px-5 py-3.5 font-mono text-slate-600">{s.mobile}</td>
                    <td className="px-5 py-3.5 text-slate-600">{s.level_name}</td>
                    <td className="px-5 py-3.5 text-slate-600 text-xs">{s.levels_registered || "—"}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={s.payment_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ClipboardCount(props) { return <Users {...props} />; }

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-head font-bold text-slate-800 text-sm mt-0.5">{value}</div>
    </div>
  );
}
function BigStat({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
      <div className="font-head text-2xl font-extrabold text-slate-900 mt-3">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </Card>
  );
}
function Breadcrumb({ items }) {
  return (
    <div className="flex items-center gap-2 text-sm mb-5 flex-wrap">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <ChevronRight className="w-4 h-4 text-slate-300" />}
          {it.onClick ? <button onClick={it.onClick} className="text-blue-600 hover:underline font-medium flex items-center gap-1">{i === 0 && <ArrowLeft className="w-4 h-4" />}{it.label}</button>
            : <span className="text-slate-700 font-semibold">{it.label}</span>}
        </span>
      ))}
    </div>
  );
}
function SchoolDialog({ dlg, setDlg, editing, form, setForm, saving, save }) {
  return (
    <Dialog open={dlg} onOpenChange={setDlg}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit School" : "Add School"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className={labelCls}>School Name *</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>City</label><input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><label className={labelCls}>State</label><input className={inputCls} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Contact Person</label><input className={inputCls} value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><label className={labelCls}>Contact Number</label><input className={inputCls} value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <button className={btnSecondary} onClick={() => setDlg(false)}>Cancel</button>
          <button className={btnPrimary} onClick={save} disabled={saving} data-testid="school-save-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
